import type { ComponentProps, ReactNode } from "react";
import { cn } from "~/lib/cn";
import { AppLink, type AppLinkProps } from "./AppLink";
import { Button } from "./Button";

/** The glyphs the public surfaces use: forward, back, a new tab, start over. */
type ArrowGlyph = "→" | "←" | "↗" | "↺";

interface ArrowLabelProps {
  /** The glyph after the text (or before it with `arrowFirst`); none renders the text alone. */
  arrow?: ArrowGlyph;
  /** A back or restart link reads glyph first («← Atrás»). */
  arrowFirst?: boolean;
  /** An icon in the glyph's place (the testimonials' external-link mark). */
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * T872 — the public text link with an arrow (`.pub-nav-link`, the October delivery's
 * `NavLink`): «Conocer Ritual Core →», «← Atrás», «Ver las fases ↗», «↺ Volver a empezar».
 * One component so the hover lives in one place (the founder, 2026-10-09: `aplicar el
 * subrayado únicamente al texto, excluyendo el icono`): the text sits in `.pub-nav-text`,
 * which carries the underline, and the glyph in `.pub-nav-arrow`, which never does. The
 * whole box stays the control — the click area and the focus ring belong to the link
 * itself, not to the text — so a press on the arrow still navigates.
 *
 * `ArrowLink` renders a router `AppLink` (`routeKey` / `to`) or, with `href`, a plain
 * anchor for an in-page hash or an external URL; `ArrowButton` is the same label on an
 * unstyled `Button` for an action that stays on the page (back, restart).
 */
function ArrowLabel({ arrow, arrowFirst = false, icon, children }: ArrowLabelProps) {
  const mark = icon ?? (arrow ? <span className="pub-nav-arrow" aria-hidden="true">{arrow}</span> : null);
  return (
    <>
      {arrowFirst ? mark : null}
      <span className="pub-nav-text">{children}</span>
      {arrowFirst ? null : mark}
    </>
  );
}

type ArrowAnchorProps = Omit<ComponentProps<"a">, "children"> & { href: string; routeKey?: never; to?: never };
export type ArrowLinkProps = ArrowLabelProps & (Omit<AppLinkProps, "children"> | ArrowAnchorProps);

export function ArrowLink({ arrow, arrowFirst, icon, children, className, ...rest }: ArrowLinkProps) {
  const label = (
    <ArrowLabel arrow={arrow} arrowFirst={arrowFirst} icon={icon}>
      {children}
    </ArrowLabel>
  );
  if ("href" in rest && typeof rest.href === "string") {
    const { routeKey: _routeKey, to: _to, ...anchor } = rest;
    return (
      <a className={cn("pub-nav-link", className)} {...anchor}>
        {label}
      </a>
    );
  }
  const link = rest as AppLinkProps;
  return (
    <AppLink className={cn("pub-nav-link", className)} {...link}>
      {label}
    </AppLink>
  );
}

export type ArrowButtonProps = ArrowLabelProps & Omit<ComponentProps<"button">, "children"> & { onClick: () => void };

export function ArrowButton({ arrow, arrowFirst, icon, children, className, type = "button", ...rest }: ArrowButtonProps) {
  return (
    <Button type={type} variant="unstyled" className={cn("pub-nav-link", className)} {...rest}>
      <ArrowLabel arrow={arrow} arrowFirst={arrowFirst} icon={icon}>
        {children}
      </ArrowLabel>
    </Button>
  );
}
